import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addPoint,
  deletePoint,
  listPoints,
  reorderPoints,
  updatePoint,
} from '../../shared/api/points'
import type { CreatePointInput, UpdatePointInput } from '../../shared/api/types'

export const pointKeys = {
  byTrip: (tripId: string) => ['points', tripId] as const,
}

export function usePoints(tripId: string | undefined) {
  return useQuery({
    queryKey: pointKeys.byTrip(tripId ?? 'unknown'),
    queryFn: () => listPoints(tripId as string),
    enabled: Boolean(tripId),
  })
}

export function useAddPoint(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePointInput) => addPoint(tripId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: pointKeys.byTrip(tripId) }),
  })
}

export function useUpdatePoint(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pointId, patch }: { pointId: string; patch: UpdatePointInput }) =>
      updatePoint(tripId, pointId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: pointKeys.byTrip(tripId) }),
  })
}

export function useDeletePoint(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pointId: string) => deletePoint(tripId, pointId),
    onSuccess: () => qc.invalidateQueries({ queryKey: pointKeys.byTrip(tripId) }),
  })
}

export function useReorderPoints(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (orderedIds: string[]) => reorderPoints(tripId, orderedIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: pointKeys.byTrip(tripId) }),
  })
}
